// src/pages/TurnosFijos.tsx
import React, { useState, ChangeEvent } from "react";
import {
  Box,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  IconButton,
  Button,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Badge,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import FilterListIcon from "@mui/icons-material/FilterList";
import { useNavigate } from "react-router-dom";
import { SelectChangeEvent } from "@mui/material/Select";
import { Chip } from "@mui/material";

// Componente TimeSelect: Permite seleccionar hora y minutos (sólo "00" y "30") o dejar en blanco
interface TimeSelectProps {
  value: string; // Formato "HH:mm" o vacío
  onChange: (value: string) => void;
}
const TimeSelect: React.FC<TimeSelectProps> = ({ value, onChange }) => {
  const [hour, setHour] = useState<string>("");
  const [minute, setMinute] = useState<string>("");

  React.useEffect(() => {
    if (value) {
      const parts = value.split(":");
      if (parts.length === 2) {
        setHour(parts[0]);
        setMinute(parts[1]);
      }
    } else {
      setHour("");
      setMinute("");
    }
  }, [value]);

  const hourOptions = Array.from({ length: 24 }, (_, i) =>
    i.toString().padStart(2, "0")
  );
  const minuteOptions = ["00", "30"];

  const handleHourChange = (e: SelectChangeEvent<string>) => {
    const newHour = e.target.value;
    setHour(newHour);
    if (newHour && minute) {
      onChange(`${newHour}:${minute}`);
    } else {
      onChange("");
    }
  };

  const handleMinuteChange = (e: SelectChangeEvent<string>) => {
    const newMinute = e.target.value;
    setMinute(newMinute);
    if (hour && newMinute) {
      onChange(`${hour}:${newMinute}`);
    } else {
      onChange("");
    }
  };

  return (
    <Box sx={{ display: "flex", gap: 2 }}>
      <FormControl sx={{ minWidth: 80 }}>
        <InputLabel id="hour-filter-label">Hora</InputLabel>
        <Select
          labelId="hour-filter-label"
          label="Hora"
          value={hour}
          onChange={handleHourChange}
        >
          <MenuItem value="">Ninguno</MenuItem>
          {hourOptions.map((hr) => (
            <MenuItem key={hr} value={hr}>
              {hr}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl sx={{ minWidth: 80 }}>
        <InputLabel id="minute-filter-label">Minutos</InputLabel>
        <Select
          labelId="minute-filter-label"
          label="Minutos"
          value={minute}
          onChange={handleMinuteChange}
        >
          <MenuItem value="">Ninguno</MenuItem>
          {minuteOptions.map((min) => (
            <MenuItem key={min} value={min}>
              {min}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );
};

interface FixedTurno {
  id: number;
  titular: string;
  diaSemana: string;
  hora: string;
  cancha: string;
  estado: string;
}

const mockFixedTurnos: FixedTurno[] = [
  {
    id: 1,
    titular: "Juan Pérez",
    diaSemana: "Lunes",
    hora: "10:00",
    cancha: "Cancha 1",
    estado: "Activo",
  },
  {
    id: 2,
    titular: "María López",
    diaSemana: "Martes",
    hora: "12:00",
    cancha: "Cancha 2",
    estado: "Cancelado",
  },
  {
    id: 3,
    titular: "Carlos Ruiz",
    diaSemana: "Miércoles",
    hora: "14:00",
    cancha: "Cancha 1",
    estado: "Programado",
  },
  {
    id: 4,
    titular: "Ana Gómez",
    diaSemana: "Jueves",
    hora: "16:00",
    cancha: "Cancha 3",
    estado: "ActivoHasta",
  },
];

const diasSemanaOpciones = [
  { value: "", label: "Todos" },
  { value: "lunes", label: "Lunes" },
  { value: "martes", label: "Martes" },
  { value: "miercoles", label: "Miércoles" },
  { value: "jueves", label: "Jueves" },
  { value: "viernes", label: "Viernes" },
  { value: "sabado", label: "Sábado" },
  { value: "domingo", label: "Domingo" },
];

const canchasOpciones = ["", "Cancha 1", "Cancha 2", "Cancha 3"];

const estadosOpciones = [
  { value: "", label: "Todos" },
  { value: "Activo", label: "Activo" },
  { value: "Cancelado", label: "Cancelado" },
  { value: "ActivoHasta", label: "Activo hasta este mes" },
  { value: "Programado", label: "Programado" },
];

const renderEstadoChip = (estado: string) => {
  switch (estado) {
    case "Activo":
      return <Chip label="Activo" color="success" />;
    case "Cancelado":
      return <Chip label="Cancelado" color="error" />;
    case "ActivoHasta":
      return <Chip label="Activo hasta este mes" color="warning" />;
    case "Programado":
      return <Chip label="Activo a partir del próximo mes" color="info" />;
    default:
      return <Chip label={estado} />;
  }
};

const TurnosFijos: React.FC = () => {
  const [fixedTurnos, setFixedTurnos] = useState<FixedTurno[]>(mockFixedTurnos);
  const navigate = useNavigate();

  // Estados para filtros
  const [filterDiaSemana, setFilterDiaSemana] = useState<string>("");
  const [filterHora, setFilterHora] = useState<string>("");
  const [filterCancha, setFilterCancha] = useState<string>("");
  const [filterEstado, setFilterEstado] = useState<string>("");

  // Estado para mostrar el modal de filtros
  const [openFilterModal, setOpenFilterModal] = useState<boolean>(false);

  const handleDelete = (id: number) => {
    if (window.confirm("¿Está seguro que desea eliminar este turno fijo?")) {
      setFixedTurnos((prev) => prev.filter((turno) => turno.id !== id));
    }
  };

  const handleOpenFilterModal = () => {
    setOpenFilterModal(true);
  };
  const handleCloseFilterModal = () => {
    setOpenFilterModal(false);
  };

  const getActiveFilterCount = () => {
    let count = 0;
    if (filterDiaSemana) count++;
    if (filterHora) count++;
    if (filterCancha) count++;
    if (filterEstado) count++;
    return count;
  };

  // Manejo de filtros en el modal
  const handleFilterDiaSemana = (e: SelectChangeEvent<string>) => {
    setFilterDiaSemana(e.target.value);
  };
  const handleFilterHora = (newTime: string) => {
    setFilterHora(newTime);
  };
  const handleFilterCancha = (e: SelectChangeEvent<string>) => {
    setFilterCancha(e.target.value);
  };
  const handleFilterEstado = (e: SelectChangeEvent<string>) => {
    setFilterEstado(e.target.value);
  };

  const filteredTurnos = fixedTurnos.filter((turno) => {
    const matchDia = filterDiaSemana
      ? turno.diaSemana === filterDiaSemana
      : true;
    const matchHora = filterHora ? turno.hora === filterHora : true;
    const matchCancha = filterCancha ? turno.cancha === filterCancha : true;
    const matchEstado = filterEstado ? turno.estado === filterEstado : true;
    return matchDia && matchHora && matchCancha && matchEstado;
  });

  return (
    <Box sx={{ p: 2 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2,
        }}
      >
        <Typography variant="h4">Turnos Fijos</Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={() => navigate("/add-turno")}
        >
          Agregar Nuevo Turno Fijo
        </Button>
      </Box>
      <Divider sx={{ mb: 2 }} />
      {/* Subtítulo y botón de filtros en la misma línea */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2,
        }}
      >
        <Typography variant="subtitle1" color="text.secondary">
          Gestión de turnos fijos por titular
        </Typography>
        <IconButton onClick={handleOpenFilterModal}>
          <Badge badgeContent={getActiveFilterCount()} color="primary">
            <FilterListIcon />
          </Badge>
        </IconButton>
      </Box>
      <Paper sx={{ mt: 2 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Titular</TableCell>
              <TableCell>Día de la semana</TableCell>
              <TableCell>Hora</TableCell>
              <TableCell>Cancha</TableCell>
              <TableCell>Estado</TableCell>
              <TableCell>Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredTurnos.map((turno) => (
              <TableRow key={turno.id}>
                <TableCell>{turno.titular}</TableCell>
                <TableCell>{turno.diaSemana}</TableCell>
                <TableCell>{turno.hora}</TableCell>
                <TableCell>{turno.cancha}</TableCell>
                <TableCell>{renderEstadoChip(turno.estado)}</TableCell>
                <TableCell>
                  <IconButton
                    onClick={() => handleDelete(turno.id)}
                    color="error"
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {filteredTurnos.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  No se encontraron turnos fijos.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      {/* Modal de filtros */}
      <Dialog
        open={openFilterModal}
        onClose={handleCloseFilterModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Filtros</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <FormControl fullWidth>
              <InputLabel id="filter-diaSemana-label">
                Día de la semana
              </InputLabel>
              <Select
                labelId="filter-diaSemana-label"
                label="Día de la semana"
                value={filterDiaSemana}
                onChange={handleFilterDiaSemana}
              >
                {diasSemanaOpciones.map((dia) => (
                  <MenuItem key={dia.value} value={dia.value}>
                    {dia.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box>
              <Typography variant="body2" gutterBottom>
                Hora
              </Typography>
              <TimeSelect value={filterHora} onChange={handleFilterHora} />
            </Box>
            <FormControl fullWidth>
              <InputLabel id="filter-cancha-label">Cancha</InputLabel>
              <Select
                labelId="filter-cancha-label"
                label="Cancha"
                value={filterCancha}
                onChange={handleFilterCancha}
              >
                {canchasOpciones.map((cancha) => (
                  <MenuItem key={cancha} value={cancha}>
                    {cancha === "" ? "Todas" : cancha}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel id="filter-estado-label">Estado</InputLabel>
              <Select
                labelId="filter-estado-label"
                label="Estado"
                value={filterEstado}
                onChange={handleFilterEstado}
              >
                {estadosOpciones.map((estado) => (
                  <MenuItem key={estado.value} value={estado.value}>
                    {estado.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseFilterModal} color="primary">
            Aplicar
          </Button>
          <Button
            onClick={() => {
              setFilterDiaSemana("");
              setFilterHora("");
              setFilterCancha("");
              setFilterEstado("");
              handleCloseFilterModal();
            }}
            sx={{ color: "grey.500" }}
          >
            Limpiar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TurnosFijos;
